import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { CreditAccountUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CreditTierCreate } from "../../../creditTier/components/create/CreditTierCreate";
import { CreditTierCreateStore } from "../../../creditTier/store";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type CreditAccountUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  tierSelectedValue?: { displayId: string; id: string } | null;
  onTierChange?: (value: string) => void;
  onTierSelected?: (value: { displayId: string; id: string } | null) => void;
  tierOptions?: { displayId: string; id: string }[];
  tierError?: string;
  movementsRows: CrudRow[];
  movementsVisibleColumns: GridColumn[];
  creditAccountUpdateStore: CreditAccountUpdateStore;
};

export const CreditAccountUpdateForm: React.FC<CreditAccountUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onTierSelected = () => {},
      tierOptions = [],
      tierSelectedValue = null,
      tierError,
      onTierChange = () => {},
      movementsRows,
      movementsVisibleColumns,
      creditAccountUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const tierIsVisible = ["SysAdmin"].some((role) =>
        rootStore.authStore.hasRole(role)
      );
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="CreditAccount"
              mode="update"
              values={{}}
              options={{ tier: tierOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "tier":
                    onTierSelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={creditAccountUpdateStore.getCardErrors("null")}
              title={langStore.groupCreditAccountLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                {tierIsVisible && (
                  <InputReference
                    id="tier"
                    label={langStore.CreditAccountTierLabel}
                    autoFocus
                    error={Boolean(tierError)}
                    helperText={
                      tierError ||
                      creditAccountUpdateStore.warnings["tier"]?.message
                    }
                    selectedValue={tierSelectedValue}
                    onChange={(value: string) => onTierChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onTierSelected(value)}
                    options={tierOptions}
                    description={langStore.CreditAccountTierDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "creditTier_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CreditTierCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CreditTierCreate
                            creditTierCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCreditTier"
                        cancelMethod="cancelNewCreditTier"
                      />
                    )}
                  />
                )}
                <ShowItemsCompact
                  label={langStore.CreditAccountMovementsLabel}
                  description={langStore.CreditAccountMovementsDescription}
                  showLabel={true}
                  rows={movementsRows}
                  columns={movementsVisibleColumns}
                  entityName="CreditMovement"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("creditMovement", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
